import React, { useRef, useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Confetti from "react-confetti";
import { ScratchCardApi, ScratchCardConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { ExternalLink } from "lucide-react";

export function GamificationScratchCard() {
  const { toast } = useToast();
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isScratching, setIsScratching] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [wonPoints, setWonPoints] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSize, setConfettiSize] = useState({ width: 0, height: 0 });
  const [isCollected, setIsCollected] = useState(false);
  const [isCollecting, setIsCollecting] = useState(false);
  const [scratchCardConfigs, setScratchCardConfigs] = useState<ScratchCardConfig[]>([]);
  const [selectedScratchCardId, setSelectedScratchCardId] = useState<string>("");

  // Get selected scratch card config
  const selectedScratchCard = useMemo(() => {
    return scratchCardConfigs.find((config) => String(config.id) === selectedScratchCardId);
  }, [scratchCardConfigs, selectedScratchCardId]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const scratchedAreaRef = useRef(0);
  const totalAreaRef = useRef(0);
  const rewardRef = useRef<number | null>(null);

  useEffect(() => {
    const updateSize = () => {
      if (typeof window !== "undefined") {
        setConfettiSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed || !selectedScratchCard) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Get the container dimensions
    const rect = canvas.getBoundingClientRect();
    const width = rect.width || 400; // fallback width
    const height = rect.height || 256; // fallback height (h-64 = 256px)

    // Set canvas size (important: set both width/height and style)
    canvas.width = width;
    canvas.height = height;

    // Draw scratchable overlay
    ctx.fillStyle = "#9ca3af"; // Grey overlay
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw "Scratch Here" text
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 24px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Scratch Here", canvas.width / 2, canvas.height / 2);

    // Calculate total area
    totalAreaRef.current = canvas.width * canvas.height;
    scratchedAreaRef.current = 0;

    // Generate random reward if not already set
    if (rewardRef.current === null && selectedScratchCard) {
      // Randomly select from smallReward, mediumReward, or bigReward
      // Each reward has equal probability (33.33% each)
      const randomValue = Math.random(); // 0 to 1
      if (randomValue < 0.333) {
        rewardRef.current = selectedScratchCard.smallReward;
      } else if (randomValue < 0.666) {
        rewardRef.current = selectedScratchCard.mediumReward;
      } else {
        rewardRef.current = selectedScratchCard.bigReward;
      }
    } else if (rewardRef.current === null) {
      // Fallback if no scratch card selected
      rewardRef.current = 0;
    }
  };

  useEffect(() => {
    if (canvasRef.current && !isRevealed && selectedScratchCard) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeCanvas();
      }, 100);

      // Also try on next frame
      requestAnimationFrame(() => {
        initializeCanvas();
      });

      return () => clearTimeout(timer);
    }
  }, [isRevealed, selectedScratchCard]);

  // Reinitialize on window resize
  useEffect(() => {
    const handleResize = () => {
      if (!isRevealed) {
        initializeCanvas();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isRevealed]);

  // Fetch scratch card configs on mount
  useEffect(() => {
    fetchScratchCardConfigs();
  }, []);

  const fetchScratchCardConfigs = async () => {
    try {
      const response = await ScratchCardApi.getAllScratchCardConfig({});

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        setScratchCardConfigs(response.scratchCard || []);
      }
    } catch (error: any) {
      console.error("Error fetching scratch card configs:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch scratch card configurations",
        variant: "destructive",
      });
    }
  };

  const handleSendOtp = () => {
    if (!mobile || mobile.length < 7 || mobile.length > 15) {
      toast({
        title: "Invalid mobile number",
        description: "Please enter a valid mobile number (7-15 digits).",
        variant: "destructive",
      });
      return;
    }
    setOtpSent(true);
    setIsOtpVerified(false);
    setOtp("");
    toast({
      title: "OTP sent",
      description: "A test OTP has been 'sent' to the entered mobile number.",
    });
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length < 4) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid OTP.",
        variant: "destructive",
      });
      return;
    }

    if (otp !== "1992") {
      toast({
        title: "Incorrect OTP",
        description: "Please enter the correct OTP.",
        variant: "destructive",
      });
      setIsOtpVerified(false);
      return;
    }

    setIsOtpVerified(true);
    toast({
      title: "OTP verified",
      description: "You can now scratch the card.",
    });
  };

  const getEventCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const scratchAt = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed || !isOtpVerified || !selectedScratchCard) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Ensure canvas is initialized
    if (canvas.width === 0 || canvas.height === 0) {
      initializeCanvas();
      return;
    }

    const radius = 30; // Scratch radius
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    // Calculate scratched area (check every 10th pixel for performance)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparentPixels = 0;
    const data = imageData.data;
    for (let i = 3; i < data.length; i += 40) {
      // Check every 10th pixel (i += 40 = 10 pixels * 4 bytes)
      if (data[i] === 0) {
        transparentPixels += 10; // Approximate
      }
    }
    scratchedAreaRef.current = transparentPixels;

    // Check if enough is scratched (30% threshold)
    const scratchedPercentage =
      (scratchedAreaRef.current / totalAreaRef.current) * 100;
    if (scratchedPercentage >= 30 && !isRevealed) {
      revealReward();
    }
  };

  const revealReward = () => {
    setIsRevealed(true);
    setWonPoints(rewardRef.current);
    if (rewardRef.current && rewardRef.current > 0) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 8000);
    }
    toast({
      title: "Reward Revealed!",
      description: `You won ${rewardRef.current} points!`,
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (isRevealed || !isOtpVerified || !selectedScratchCard) return;
    // Only start drawing on left mouse button (button 0)
    if (e.button === 0) {
      isDrawingRef.current = true;
      setIsScratching(true);
      const coords = getEventCoordinates(e);
      if (coords) {
        scratchAt(coords.x, coords.y);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Only scratch if mouse button is held down and verified
    if (!isDrawingRef.current || isRevealed || !isOtpVerified || !selectedScratchCard) return;
    e.preventDefault();
    const coords = getEventCoordinates(e);
    if (coords) {
      scratchAt(coords.x, coords.y);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    isDrawingRef.current = false;
    setIsScratching(false);
  };

  // Global mouse up handler to stop drawing if mouse is released outside canvas
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDrawingRef.current = false;
      setIsScratching(false);
    };

    if (!isRevealed) {
      window.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        window.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isRevealed]);

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (isRevealed || !isOtpVerified || !selectedScratchCard) return;
    e.preventDefault();
    isDrawingRef.current = true;
    setIsScratching(true);
    const coords = getEventCoordinates(e);
    if (coords) scratchAt(coords.x, coords.y);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || isRevealed || !isOtpVerified || !selectedScratchCard) return;
    e.preventDefault();
    const coords = getEventCoordinates(e);
    if (coords) scratchAt(coords.x, coords.y);
  };

  const handleTouchEnd = () => {
    isDrawingRef.current = false;
    setIsScratching(false);
  };

  const handleCollectReward = async () => {
    if (wonPoints === null || !selectedScratchCard) {
      return;
    }

    if (!mobile || !isOtpVerified) {
      toast({
        title: "Verification required",
        description: "Please verify your mobile number and OTP before collecting reward.",
        variant: "destructive",
      });
      return;
    }

    setIsCollecting(true);
    try {
      const payload = {
        p_MobileNo: mobile,
        p_OTP: "1992", // using the same test OTP for now
        p_PointsORDiscountWon: wonPoints,
        p_ScratchCardId: selectedScratchCard.id,
      };

      const response = await ScratchCardApi.insertScratchCardTransaction(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        setIsCollected(true);
        toast({
          title: "Reward Collected!",
          description: response.message || `${wonPoints} points have been added to your account.`,
        });
        // Reset all input fields after successful collection
        // Keep isCollected as true to show success message
        setIsOtpVerified(false);
        setOtpSent(false);
        setOtp("");
        setMobile("");
        setSelectedScratchCardId("");
        setWonPoints(null);
        setIsRevealed(false);
        rewardRef.current = null;
        scratchedAreaRef.current = 0;
      }
    } catch (error: any) {
      console.error("Error collecting reward:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to collect reward.",
        variant: "destructive",
      });
    } finally {
      setIsCollecting(false);
    }
  };

  const handleReset = () => {
    setIsRevealed(false);
    setWonPoints(null);
    setIsCollected(false);
    scratchedAreaRef.current = 0;
    rewardRef.current = null;
    // Canvas will be reinitialized by useEffect when isRevealed becomes false
  };

  return (
    <div className="space-y-6 relative">
      {showConfetti && confettiSize.width > 0 && confettiSize.height > 0 && (
        <Confetti
          width={confettiSize.width}
          height={confettiSize.height}
          numberOfPieces={300}
          recycle={false}
        />
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">Scratch Card</h2>
          <p className="text-sm text-muted-foreground">
            Verify your mobile number and scratch the card to reveal your reward.
          </p>
        </div>
        <Button variant="outline" size="sm" className="w-fit shrink-0" asChild>
          <a
            href="/scratch-card"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Public page
          </a>
        </Button>
      </div>

      {/* Mobile & OTP Section */}
      <Card>
        <CardHeader>
          <CardTitle>Mobile Verification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mobile Number</label>
            <div className="flex gap-2">
              <Input
                type="tel"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setMobile(value);
                }}
                maxLength={15}
              />
              <Button type="button" variant="outline" onClick={handleSendOtp}>
                Send OTP
              </Button>
            </div>
          </div>

          {otpSent && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Enter OTP</label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setOtp(value);
                  }}
                  maxLength={6}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVerifyOtp}
                >
                  Verify
                </Button>
              </div>
              {isOtpVerified && (
                <p className="text-xs text-emerald-600 font-medium">
                  OTP verified. You can scratch the card now.
                </p>
              )}
            </div>
          )}

          {isOtpVerified && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Scratch Card *</label>
              <Select
                value={selectedScratchCardId}
                onValueChange={(value) => {
                  setSelectedScratchCardId(value);
                  // Reset scratch state when changing card
                  setIsRevealed(false);
                  setWonPoints(null);
                  setIsCollected(false);
                  rewardRef.current = null;
                  scratchedAreaRef.current = 0;
                }}
                disabled={isRevealed && !isCollected}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a scratch card" />
                </SelectTrigger>
                <SelectContent>
                  {scratchCardConfigs.map((config) => (
                    <SelectItem key={config.id} value={String(config.id)}>
                      {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedScratchCardId && (
                <p className="text-xs text-gray-500">
                  Please select a scratch card to continue.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scratch Card Section */}
      <Card>
        <CardHeader>
          <CardTitle>Scratch Card</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-full max-w-md">
              <div className="relative rounded-lg overflow-hidden border-2 border-gray-300 bg-gradient-to-br from-blue-50 to-purple-50">
                {/* Show messages when not ready to scratch */}
                {!isRevealed && !isOtpVerified && (
                  <div className="w-full h-64 flex items-center justify-center bg-gray-100">
                    <p className="text-sm text-gray-500 text-center px-4">
                      Please verify your mobile number and OTP to scratch the card
                    </p>
                  </div>
                )}
                {!isRevealed && isOtpVerified && !selectedScratchCard && (
                  <div className="w-full h-64 flex items-center justify-center bg-gray-100">
                    <p className="text-sm text-gray-500 text-center px-4">
                      Please select a scratch card to continue
                    </p>
                  </div>
                )}

                {/* Reward Display (hidden behind scratch layer) - only show when ready to scratch */}
                {!isRevealed && isOtpVerified && selectedScratchCard && (
                  <>
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-yellow-50 to-orange-50">
                      <div className="text-center space-y-2">
                        <p className="text-sm text-gray-600 font-medium">
                          You Won!
                        </p>
                        <p className="text-4xl font-bold text-emerald-600">
                          ?
                        </p>
                      </div>
                    </div>

                    {/* Scratchable Canvas Overlay */}
                    <canvas
                      ref={canvasRef}
                      className="w-full h-64 cursor-crosshair touch-none relative z-10"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={(e) => {
                        isDrawingRef.current = false;
                        setIsScratching(false);
                      }}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      style={{
                        display: "block",
                        userSelect: "none",
                        pointerEvents: "auto",
                      }}
                    />
                  </>
                )}

                {/* Revealed State */}
                {isRevealed && (
                  <div className="w-full h-64 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-yellow-50 to-orange-50">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-gray-600 font-medium">
                        You Won!
                      </p>
                      <p className="text-4xl font-bold text-emerald-600">
                        {wonPoints} Points
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {!isRevealed && isOtpVerified && selectedScratchCard && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  Scratch to Reveal Your Reward!
                </p>
              )}
            </div>

            {isRevealed && !isCollected && (
              <Button
                onClick={handleCollectReward}
                className="w-full max-w-md bg-orange-600 text-white hover:bg-orange-700"
                size="lg"
                disabled={isCollecting || !selectedScratchCard}
              >
                {isCollecting ? "Collecting..." : "Collect Reward"}
              </Button>
            )}

            {isRevealed && isCollected && (
              <div className="w-full max-w-md space-y-2">
                <p className="text-center text-sm text-emerald-600 font-medium">
                  Reward collected successfully!
                </p>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="w-full"
                >
                  Scratch Another Card
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

